<template>
  <div
    v-if="showing == 'html' && icon"
    id="code-snippet-html"
    role="tabpanel"
    aria-labelledby="tab-html"
  >
    <owa-code-block
      add-highlighting
      allow-shadow-copy
      copy-text="Copy Code Snippet"
      custom-copy-icon="faClone"
      copy-tracking-event="icon_html_code_copy_button_click"
      :copy-tracking-params="{ icon_name: icon.name, icon_style: styling.style, icon_family: styling.family }"
      lang="markup"
      :code="`<i class=&quot;${translatedStyle} fa-${icon.name}${translatedAnimation}${translatedRotation}${translatedSize}&quot;${translatedIconStyles}></i>`"
    />

    <icon-code-snippet-links
      :has-custom-styling="hasCustomStyling"
      :selected-style="styling.style"
      :major-version="majorVersion"
    />
  </div>
</template>

<script>
  import { transformIconColoring } from './utils.js'
  import trimStart from 'lodash/trimStart'
  import IconCodeSnippetLinks from './IconCodeSnippetLinks'
  import { removeOpacity } from 'app/views/search/colorUtils'

  export default {
    name: 'IconCodeSnippetHtml',

    components: {
      IconCodeSnippetLinks
    },

    props: {
      defaultStyles: {
        type: Object
      },
      hasCustomStyling: {
        type: Boolean
      },
      icon: {
        type: Object
      },
      isDuotone: {
        type: Boolean
      },
      showing: {
        required: false
      },
      styling: {
        type: Object
      },
      translatedStyle: {
        required: true
      },
      majorVersion: {
        type: Number
      }
    },

    data() {
      return {}
    },

    computed: {
      hasCustomRotation() {
        return Number.isInteger(parseInt(this.styling?.rotation))
      },

      translatedAnimation() {
        if (!this.styling?.animation) {
          return ''
        } else if (this.styling?.animation === 'spin-reverse') {
          return ` fa-spin fa-${this.styling.animation}`
        } else {
          return ` fa-${this.styling.animation}`
        }
      },

      translatedRotation() {
        if (!this.styling?.rotation) {
          return ''
        } else {
          if (this.hasCustomRotation) {
            return ' fa-rotate-by'
          } else {
            return ` fa-${this.styling.rotation}`
          }
        }
      },

      translatedSize() {
        if (!this.styling?.size) {
          return ''
        } else {
          return ` fa-${this.styling.size}`
        }
      },

      colorPrimary() {
        if (this.isDuotone && this.styling?.colorPrimary !== this.defaultStyles.colorPrimary) {
          return ` --fa-primary-color: ${removeOpacity(this.styling.colorPrimary)};`
        } else if (this.styling?.colorPrimary !== this.defaultStyles.colorPrimary) {
          return ` color: ${removeOpacity(this.styling.colorPrimary)};`
        } else {
          return ''
        }
      },

      colorSecondary() {
        if (!this.isDuotone) {
          return ''
        } else if (this.styling?.colorSecondary !== this.defaultStyles.colorSecondary) {
          return ` --fa-secondary-color: ${removeOpacity(this.styling.colorSecondary)};`
        } else {
          return ''
        }
      },

      opacityPrimary() {
        if (!this.isDuotone) {
          return ''
        } else if (this.styling?.opacityPrimary !== this.defaultStyles.opacityPrimary) {
          return ` --fa-primary-opacity: ${this.styling.opacityPrimary};`
        } else {
          return ''
        }
      },

      opacitySecondary() {
        if (!this.isDuotone) {
          return ''
        } else if (this.styling?.opacitySecondary !== this.defaultStyles.opacitySecondary) {
          return ` --fa-secondary-opacity: ${this.styling.opacitySecondary};`
        } else {
          return ''
        }
      },

      translatedIconStyles() {
        let stringStyle = transformIconColoring(
          this.styling,
          this.defaultStyles,
          this.colorPrimary,
          this.colorSecondary,
          this.opacityPrimary,
          this.opacitySecondary
        )

        if (this.hasCustomRotation) {
          stringStyle = stringStyle.concat(` --fa-rotate-angle: ${this.styling?.rotation}deg;`)
        }

        if (stringStyle) {
          return ` style="${trimStart(stringStyle)}"`
        } else {
          return ''
        }
      }
    },

    methods: {
      copyHTML() {
        this.$clipboard(
          `<i class="${this.translatedStyle} fa-${this.icon.name}${this.translatedAnimation}${this.translatedSize}${this.translatedRotation}"${
            this.translatedIconStyles ? this.translatedIconStyles : ''
          }></i>`
        )

        this.$emit('markCopied', 'html')
      }
    }
  }
</script>
